import { useMemo, useState } from 'react';
import { CreditCard, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { useCards } from '@/features/financials/hooks';
import { useProfiles } from '@/features/users/hooks';
import {
  useAllCardAssignments,
  useAssignCard,
  useUnassignCard,
} from '@/features/expenses/cardAssignments';
import { ROLE_LABELS, type Role } from '@/lib/permissions';
import type { Tables } from '@/types/database';

/**
 * Super_admin only. Lists every card synced from Mercury / Plaid and lets
 * the super_admin attach one or more profiles to each. Whoever is attached
 * to a card sees that card's transactions on the Expense Tracking page,
 * uploads receipts, picks categories, and submits for approval.
 *
 * The DB enforces this: transactions / cards / bank_accounts RLS checks
 * card_assignments, and the receipts storage bucket policy decodes the
 * path's transaction_id segment and runs through the same predicate. See
 * supabase/migrations/21_expense_tracking.sql + 22_receipts_storage.sql.
 */
export function CardMappingPage() {
  const { data: cards, isLoading: cardsLoading } = useCards();
  const { data: profiles, isLoading: profilesLoading } = useProfiles();
  const { data: assignments, isLoading: assignmentsLoading } = useAllCardAssignments();
  const assign = useAssignCard();
  const unassign = useUnassignCard();

  const [pendingProfileByCard, setPendingProfileByCard] = useState<Record<string, string>>({});

  const assignmentsByCard = useMemo(() => {
    const m = new Map<
      string,
      Array<{ profile_id: string; profile: ProfileLike | null }>
    >();
    for (const a of assignments ?? []) {
      const arr = m.get(a.card_id) ?? [];
      arr.push({ profile_id: a.profile_id, profile: a.profiles });
      m.set(a.card_id, arr);
    }
    return m;
  }, [assignments]);

  const eligibleProfiles = useMemo(() => {
    return (profiles ?? []).filter((p) => p.is_active);
  }, [profiles]);

  const isLoading = cardsLoading || profilesLoading || assignmentsLoading;

  const onAssign = async (cardId: string) => {
    const profileId = pendingProfileByCard[cardId];
    if (!profileId) {
      toast.error('Pick a person first.');
      return;
    }
    try {
      await assign.mutateAsync({ card_id: cardId, profile_id: profileId });
      setPendingProfileByCard((prev) => {
        const next = { ...prev };
        delete next[cardId];
        return next;
      });
      toast.success('Card assigned.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Assign failed');
    }
  };

  const onUnassign = async (cardId: string, profileId: string) => {
    try {
      await unassign.mutateAsync({ card_id: cardId, profile_id: profileId });
      toast.success('Assignment removed.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Remove failed');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Card mapping"
      />

      <Card>
        <CardHeader>
          <CardTitle>Cards</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : (cards ?? []).length === 0 ? (
            <EmptyState
              icon={CreditCard}
              title="No cards synced yet"
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Card</TableHead>
                  <TableHead>Account</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned to</TableHead>
                  <TableHead className="w-72">Add person</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(cards ?? []).map((c) => {
                  const assigneeRows = assignmentsByCard.get(c.id) ?? [];
                  const assignedIds = new Set(assigneeRows.map((r) => r.profile_id));
                  const remaining = eligibleProfiles.filter((p) => !assignedIds.has(p.id));
                  return (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-foreground">
                            {c.name_on_card ?? 'Card'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            •••• {c.last_four ?? '????'}
                            {c.network ? ` · ${c.network}` : ''}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {c.bank_accounts?.name ?? '—'}
                      </TableCell>
                      <TableCell>
                        <CardStatusBadge status={c.status} />
                      </TableCell>
                      <TableCell>
                        {assigneeRows.length === 0 ? (
                          <span className="text-xs text-muted-foreground">Nobody yet</span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {assigneeRows.map((row) => (
                              <AssigneeChip
                                key={row.profile_id}
                                profile={row.profile}
                                onRemove={() => onUnassign(c.id, row.profile_id)}
                                disabled={unassign.isPending}
                              />
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Select
                            value={pendingProfileByCard[c.id] ?? ''}
                            onValueChange={(v) =>
                              setPendingProfileByCard((prev) => ({ ...prev, [c.id]: v }))
                            }
                            disabled={remaining.length === 0 || assign.isPending}
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue
                                placeholder={
                                  remaining.length === 0 ? 'All set' : 'Pick a person…'
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {remaining.map((p) => (
                                <SelectItem key={p.id} value={p.id}>
                                  <span className="flex items-center gap-2">
                                    <span>{p.full_name ?? p.email}</span>
                                    <Badge variant="muted" className="text-[10px]">
                                      {ROLE_LABELS[p.role as Role]}
                                    </Badge>
                                  </span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onAssign(c.id)}
                            disabled={!pendingProfileByCard[c.id] || assign.isPending}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type ProfileLike = Pick<Tables<'profiles'>, 'id' | 'full_name' | 'email' | 'avatar_url' | 'role'>;

function AssigneeChip({
  profile,
  onRemove,
  disabled,
}: {
  profile: ProfileLike | null;
  onRemove: () => void;
  disabled: boolean;
}) {
  if (!profile) {
    // The join row exists but the profile itself wasn't returned (deleted /
    // RLS hid it). Surface the raw id so the super_admin can still detach.
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 px-2.5 py-1 text-xs text-muted-foreground">
        Unknown user
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          className="rounded-full p-0.5 hover:bg-secondary"
          aria-label="Remove"
        >
          <X className="h-3 w-3" />
        </button>
      </span>
    );
  }
  const initials = (profile.full_name ?? profile.email)
    .split(/[.\s@]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/50 py-0.5 pl-1 pr-2 text-xs text-foreground">
      <Avatar className="h-5 w-5">
        {profile.avatar_url && <AvatarImage src={profile.avatar_url} alt={profile.email} />}
        <AvatarFallback className="text-[9px]">{initials}</AvatarFallback>
      </Avatar>
      <span>{profile.full_name ?? profile.email}</span>
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="rounded-full p-0.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
        aria-label="Remove"
      >
        <X className="h-3 w-3" />
      </button>
    </span>
  );
}

function CardStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const variant: 'success' | 'warning' | 'muted' =
    status === 'active'
      ? 'success'
      : status === 'frozen' || status === 'inactive' || status === 'suspended'
        ? 'warning'
        : 'muted';
  return (
    <Badge variant={variant} className="capitalize">
      {status}
    </Badge>
  );
}
